import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getAccessToken, authenticatedFetch as fetch } from '../lib/auth-context.js';
import { PROPOSAL_STATUS_LABELS, type ProposalOutput, type UpdateProposalInput } from '@1financial/shared';
import { ProposalComposerForm, type ComposerInitial, type ComposerPayload } from '../components/ProposalComposerForm.js';
import { useCapabilities } from '../hooks/useCapabilities.js';
import { fmtDate, fmtDateTime } from '../lib/date.js';
import { InvoiceFormModal } from '../components/InvoiceFormModal.js';

const STATUS_BADGE: Record<string, string> = {
  DRAFT: 'pill-neutral',
  SENT: 'pill-info',
  VIEWED: 'pill-warn',
  SENT_BACK: 'pill-warn',
  SIGNED: 'pill-success',
  DECLINED: 'pill-danger',
  EXPIRED: 'pill-neutral',
};

const SCHEDULE_LABEL: Record<string, string> = {
  MONTHLY: 'Monthly',
  QUARTERLY: 'Quarterly',
  SEMI_ANNUAL: 'Every 6 months',
  ANNUAL: 'Annual',
};

async function apiError(res: Response): Promise<Error> {
  const text = await res.text().catch(() => '');
  try {
    const body = JSON.parse(text) as { ok?: false; error?: { message?: string; code?: string } };
    const msg = body?.error?.message ?? body?.error?.code ?? res.statusText;
    return new Error(msg ? `${res.status}: ${msg}` : `Failed: ${res.status}`);
  } catch {
    return new Error(text || res.statusText || `Failed: ${res.status}`);
  }
}

export function ProposalDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [copied, setCopied] = useState(false);
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const detail = useQuery<ProposalOutput>({
    queryKey: ['proposal', id],
    queryFn: async () => {
      const res = await fetch(`/v1/proposals/${id}`, { headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` } });
      if (!res.ok) throw await apiError(res);
      const body = await res.json() as { ok: true; data: ProposalOutput };
      return body.data;
    },
  });

  const send = useMutation({
    mutationFn: async () => {
      const res = await fetch(`/v1/proposals/${id}/send`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
      });
      if (!res.ok) throw await apiError(res);
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['proposal', id] }),
  });

  const decline = useMutation({
    mutationFn: async () => {
      const res = await fetch(`/v1/proposals/${id}/decline`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
      });
      if (!res.ok) throw await apiError(res);
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['proposal', id] }),
  });

  const edit = useMutation({
    mutationFn: async (input: UpdateProposalInput) => {
      const res = await fetch(`/v1/proposals/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAccessToken() ?? ''}` },
        body: JSON.stringify(input),
      });
      if (!res.ok) throw await apiError(res);
      return res.json() as Promise<{ ok: true; data: ProposalOutput }>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['proposal', id] });
      queryClient.invalidateQueries({ queryKey: ['proposals'] });
      setEditing(false);
    },
  });

  const remove = useMutation({
    mutationFn: async () => {
      const res = await fetch(`/v1/proposals/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
      });
      if (!res.ok) throw await apiError(res);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['proposals'] });
      navigate('/proposals');
    },
  });

  const [showInvoice, setShowInvoice] = useState(false);

  const toggleInvoicing = useMutation({
    mutationFn: async (paused: boolean) => {
      const res = await fetch(`/v1/proposals/${id}/invoicing`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAccessToken() ?? ''}` },
        body: JSON.stringify({ paused }),
      });
      if (!res.ok) throw await apiError(res);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['proposal', id] }),
  });

  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);
  async function downloadPdf() {
    setPdfBusy(true);
    setPdfError(null);
    try {
      const res = await fetch(`/v1/proposals/${id}/pdf`, { headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` } });
      if (!res.ok) throw await apiError(res);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Proposal — ${p.subject}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setPdfError(err instanceof Error ? err.message : String(err));
    } finally {
      setPdfBusy(false);
    }
  }

  const { canDo } = useCapabilities();
  const canViewFees = canDo('billing.view');
  const canBill = canDo('billing.manage') && canViewFees;
  const invoiceSource = useQuery({
    queryKey: ['invoice-source', detail.data?.clientId, id],
    enabled: canBill && detail.data?.type === 'ONE_OFF' && detail.data?.status === 'SIGNED' && !!detail.data?.clientId,
    queryFn: async () => {
      const res = await fetch(`/v1/invoices/sources?${new URLSearchParams({ clientId: detail.data!.clientId!, type: 'ONE_OFF', proposalId: id! })}`, { headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` } });
      if (!res.ok) throw await apiError(res);
      return (await res.json() as { data: { items: Array<{ invoices: Array<{ id: string; deletedAt: string | null; recurringPeriodStart: string | null }> }> } }).data;
    },
  });
  const existingInvoice = invoiceSource.data?.items[0]?.invoices.find((inv) => !inv.recurringPeriodStart);

  if (detail.isLoading) return <div className="text-sm text-muted">Loading proposal…</div>;
  if (detail.error || !detail.data) return <div className="text-sm text-rose-300">Error: {detail.error?.message}</div>;

  const p = detail.data;

  const copyUrl = () => {
    if (p.signingUrl) {
      navigator.clipboard.writeText(p.signingUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="space-y-6 w-full min-w-0">
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
        <div>
          <button onClick={() => navigate(-1)} className="text-xs text-muted hover:text-white mb-2">← Back</button>
          <h1 className="text-2xl font-semibold text-white break-words">{p.subject}</h1>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <span className={`px-2 py-0.5 rounded text-xs ${STATUS_BADGE[p.status] ?? 'bg-bg-hover text-white'}`}>
              {PROPOSAL_STATUS_LABELS[p.status] ?? p.status}
            </span>
            <span className={`px-2 py-0.5 rounded text-xs ${p.type === 'RECURRING' ? 'pill-info' : 'pill-neutral'}`}>
              {p.type === 'RECURRING' ? 'Recurring' : 'One-off'}
            </span>
            <span className="text-sm text-muted">{p.clientName ?? '—'}</span>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {canDo('proposals.send') && canViewFees && p.status === 'DRAFT' && (
            <button
              onClick={() => send.mutate()}
              disabled={send.isPending}
              className="btn-ghost-info border text-sm px-3 py-1.5 rounded"
            >
              Send proposal
            </button>
          )}
          {canBill && p.type === 'ONE_OFF' && p.status === 'SIGNED' && p.items.length > 0 && (
            <button disabled={invoiceSource.isFetching} onClick={() => existingInvoice && !existingInvoice.deletedAt ? navigate(`/invoices/${existingInvoice.id}`) : setShowInvoice(true)} className="bg-accent hover:bg-emerald-400 disabled:opacity-50 text-accent-fg text-sm font-medium px-3 py-1.5 rounded-lg">
              {p.invoicedAt || existingInvoice ? 'Open invoice' : 'Generate invoice'}
            </button>
          )}
          {canBill && p.type === 'RECURRING' && p.status === 'SIGNED' && (
            <button
              onClick={() => toggleInvoicing.mutate(!p.invoicingPaused)}
              disabled={toggleInvoicing.isPending}
              className="border border-border text-white text-sm px-3 py-1.5 rounded hover:bg-bg-hover"
            >
              {p.invoicingPaused ? 'Resume invoicing' : 'Pause invoicing'}
            </button>
          )}
          <button
            onClick={() => void downloadPdf()}
            disabled={pdfBusy || !canViewFees || !canDo('proposals.view')}
            className="border border-border text-white text-sm px-3 py-1.5 rounded hover:bg-bg-hover disabled:opacity-50"
          >
            {pdfBusy ? 'Rendering…' : 'Download PDF'}
          </button>
          {canDo('proposals.edit') && canViewFees && ['SIGNED', 'DECLINED', 'EXPIRED'].indexOf(p.status) === -1 && (
            <button
              onClick={() => setEditing(true)}
              disabled={edit.isPending}
              className="bg-bg-card hover:bg-bg-hover border border-border text-white text-sm px-3 py-1.5 rounded"
            >
              Edit
            </button>
          )}
          <button
            onClick={() => setDeleting(true)}
            disabled={remove.isPending || !canDo('proposals.delete')}
            className="btn-ghost-danger border text-sm px-3 py-1.5 rounded"
          >
            Delete
          </button>
          {canDo('proposals.edit') && (p.status === 'DRAFT' || p.status === 'SENT' || p.status === 'VIEWED') && (
            <button
              onClick={() => decline.mutate()}
              disabled={decline.isPending}
              className="btn-ghost-danger border text-sm px-3 py-1.5 rounded"
            >
              Mark declined
            </button>
          )}
        </div>
      </div>

      {send.error && <div className="text-sm text-rose-300">{send.error.message}</div>}
      {decline.error && <div className="text-sm text-rose-300">{decline.error.message}</div>}
      {edit.error && <div className="text-sm text-rose-300">{edit.error.message}</div>}
      {remove.error && <div className="text-sm text-rose-300">{remove.error.message}</div>}
      {showInvoice && p.clientId && <InvoiceFormModal clientId={p.clientId} initialProposalId={p.id} onClose={() => setShowInvoice(false)} onSaved={(invoiceId) => navigate(`/invoices/${invoiceId}`)} />}
      {toggleInvoicing.error && <div className="text-sm text-rose-300">{toggleInvoicing.error.message}</div>}
      {pdfError && <div className="text-sm text-rose-300">{pdfError}</div>}

      {p.signingUrl && (
        <section className="bg-bg-panel border border-border rounded-xl p-4">
          <h2 className="text-sm font-medium text-white mb-2">Client signing link</h2>
          <div className="flex flex-wrap items-center gap-2">
            <input
              readOnly
              value={p.signingUrl}
              className="flex-1 bg-bg-card border border-border rounded-lg px-3 py-2 text-sm text-white"
            />
            <button
              onClick={copyUrl}
              className="text-sm bg-accent hover:bg-emerald-400 text-accent-fg px-3 py-1.5 rounded"
            >
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
        </section>
      )}

      {p.items.length > 0 && (
        <section className="bg-bg-panel border border-border rounded-xl p-5">
          <h2 className="text-sm font-medium text-white mb-3">
            {p.pricingMode === 'LUMP_SUM'
              ? 'Scope — lump-sum snapshot'
              : (p.type === 'RECURRING' ? 'Items — priced snapshot (per billing period)' : 'Items — priced snapshot')}
          </h2>
          <div className="divide-y divide-border">
            {p.items.map((item, idx) => (
              <div key={item.id} className="flex items-start justify-between gap-4 py-2">
                <div className="min-w-0">
                  <div className="text-sm text-white"><span className="text-muted mr-2">{idx + 1}.</span>{item.title}</div>
                  {item.description && <div className="text-xs text-muted mt-0.5 ml-6">{item.description}</div>}
                </div>
                {canViewFees && p.pricingMode !== 'LUMP_SUM' && (
                  <div className="text-sm text-white shrink-0">{item.currency} {(item.unitPrice / 100).toFixed(2)}</div>
                )}
              </div>
            ))}
          </div>
          {p.feeAmount != null && p.feeCurrency && (
            <div className="flex justify-between border-t border-border mt-2 pt-2 text-sm font-medium text-white">
              <span>{p.pricingMode === 'LUMP_SUM' ? (p.type === 'RECURRING' ? 'Lump sum per period' : 'Lump sum') : (p.type === 'RECURRING' ? 'Total per period' : 'Total')}</span>
              <span>{p.feeCurrency} {(p.feeAmount / 100).toFixed(2)}</span>
            </div>
          )}
          <p className="text-xs text-muted mt-2">Snapshot — later edits to the source records do not change this proposal.</p>
        </section>
      )}

      {p.type === 'RECURRING' && (
        <section className="bg-bg-panel border border-border rounded-xl p-5">
          <h2 className="text-sm font-medium text-white mb-2">Billing schedule</h2>
          <div className="text-sm text-white space-y-1">
            <div>{SCHEDULE_LABEL[p.billingSchedule ?? ''] ?? p.billingSchedule ?? '—'}</div>
            {p.billingStartDate && <div className="text-muted">First period starts: {fmtDate(p.billingStartDate)}</div>}
            {p.status === 'SIGNED' && p.nextInvoiceDate && (
              <div className="text-muted">Next draft invoice: {fmtDate(p.nextInvoiceDate)}</div>
            )}
            {p.status === 'SIGNED' && (
              <div className={p.invoicingPaused ? 'text-amber-300' : 'text-emerald-300'}>
                {p.invoicingPaused ? 'Draft generation paused' : 'Drafts generate automatically (review before issuing — never auto-sent)'}
              </div>
            )}
          </div>
        </section>
      )}

      {p.items.length === 0 && (
        <section className="bg-bg-panel border border-border rounded-xl p-5">
          <h2 className="text-sm font-medium text-white mb-2">Fee</h2>
          <div className="text-sm text-white">
            {p.feeAmount != null && p.feeCurrency ? `${p.feeCurrency} ${(p.feeAmount / 100).toFixed(2)}` : '—'}
          </div>
        </section>
      )}

      {p.clauses.length > 0 && (
        <section className="bg-bg-panel border border-border rounded-xl p-5">
          <h2 className="text-sm font-medium text-white mb-3">Terms & clauses</h2>
          <div className="space-y-3">
            {p.clauses.map((clause, idx) => (
              <div key={clause.id}>
                <div className="text-sm font-medium text-white">{idx + 1}. {clause.title}</div>
                <div className="text-xs text-muted mt-1 whitespace-pre-wrap break-words max-w-prose">{clause.body}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="bg-bg-panel border border-border rounded-xl p-5">
        <h2 className="text-sm font-medium text-white mb-2">Engagement letter</h2>
        {p.engagementLetterUrl ? (
          <a href={p.engagementLetterUrl} target="_blank" rel="noreferrer" className="text-accent hover:underline text-sm break-all">
            {p.engagementLetterUrl}
          </a>
        ) : (
          <div className="text-sm text-muted">No engagement letter link yet.</div>
        )}
      </section>

      <section className="bg-bg-panel border border-border rounded-xl p-5">
        <h2 className="text-sm font-medium text-white mb-2">Body</h2>
        <div className="text-sm text-white whitespace-pre-wrap break-words max-w-prose">{p.body}</div>
      </section>

      <section className="bg-bg-panel border border-border rounded-xl p-5">
        <h2 className="text-sm font-medium text-white mb-3">Recipients</h2>
        <div className="space-y-2">
          {p.recipients.map((r, i) => (
            <div key={i} className="flex items-center justify-between gap-3 text-sm">
              <div className="min-w-0 truncate text-white" title={r.name}>{r.name}</div>
              <div className="min-w-0 break-all text-right text-muted">{r.email} · {r.role}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-bg-panel border border-border rounded-xl p-5">
        <h2 className="text-sm font-medium text-white mb-2">Timeline</h2>
        <div className="space-y-1 text-sm text-muted">
          <div>Created: {fmtDateTime(p.createdAt)}</div>
          {p.signedAt && <div className="text-emerald-300">Signed: {fmtDateTime(p.signedAt)}</div>}
          {p.declinedAt && <div className="text-rose-300">Declined: {fmtDateTime(p.declinedAt)}</div>}
          {p.invoicedAt && <div>Invoiced: {fmtDateTime(p.invoicedAt)}</div>}
          {p.expiresAt && <div>Expires: {fmtDateTime(p.expiresAt)}</div>}
        </div>
      </section>

      {editing && p && (
        <EditProposalModal
          proposal={p}
          onClose={() => setEditing(false)}
          onSubmit={(input) => edit.mutate(input)}
          submitting={edit.isPending}
        />
      )}
      {deleting && p && (
        <ConfirmModal
          title="Delete proposal"
          message={`Delete "${p.subject}"? This cannot be undone.`}
          onClose={() => setDeleting(false)}
          onConfirm={() => remove.mutate()}
          submitting={remove.isPending}
        />
      )}
    </div>
  );
}

function EditProposalModal({ proposal, onClose, onSubmit, submitting }: {
  proposal: ProposalOutput;
  onClose: () => void;
  onSubmit: (input: UpdateProposalInput) => void;
  submitting: boolean;
}) {
  // OPS 3.5 B #3: the edit card IS the creation card — same shared
  // ProposalComposerForm, same sections, same order; only client/type/
  // engagement are locked (non-reassignable) and the current status shows
  // on top. No edit-only fee/letter blocks: the fee derives from items.
  const initial: ComposerInitial = {
    clientId: proposal.clientId ?? '',
    clientOption: proposal.clientId
      ? { id: proposal.clientId, name: proposal.clientName ?? 'Client', company: null, email: proposal.clientEmail, jurisdiction: null }
      : null,
    type: proposal.type,
    engagementId: proposal.engagementId ?? '',
    currency: proposal.feeCurrency ?? 'AED',
    pricingMode: proposal.pricingMode,
    lumpSum: proposal.lumpSumAmount != null ? (proposal.lumpSumAmount / 100).toFixed(2) : '',
    items: proposal.items.map((i) => ({
      key: i.id,
      sourceType: i.sourceType,
      sourceId: i.complianceItemId ?? i.engagementSubItemId ?? null,
      title: i.title,
      description: i.description ?? '',
      price: (i.unitPrice / 100).toFixed(2),
    })),
    schedule: proposal.billingSchedule ?? 'MONTHLY',
    startDate: proposal.billingStartDate ? proposal.billingStartDate.slice(0, 10) : '',
    clauses: proposal.clauses.map((c) => ({ key: c.id, libraryClauseId: c.libraryClauseId, title: c.title, body: c.body })),
    subject: proposal.subject,
    body: proposal.body,
    expiresAt: proposal.expiresAt ? proposal.expiresAt.slice(0, 10) : '',
    // The client signer row is shown read-only (signerEmail) like in the
    // composer — keep it out of the editable list; it is re-added on submit.
    recipients: proposal.recipients
      .filter((r) => !(proposal.clientEmail && r.email.toLowerCase() === proposal.clientEmail.toLowerCase()))
      .map((r) => ({ name: r.name, email: r.email, role: r.role })),
  };

  function handleSubmit(payload: ComposerPayload) {
    // PATCH replaces the recipient set wholesale and does NOT auto-add the
    // client signer the way /compose does — re-add it here, same as create.
    const recipients: UpdateProposalInput['recipients'] = [
      ...(proposal.clientEmail
        ? [{ name: proposal.clientName ?? 'Client', email: proposal.clientEmail, role: 'SIGNER' as const }]
        : []),
      ...payload.recipients,
    ];
    onSubmit({
      subject: payload.subject,
      body: payload.body,
      feeCurrency: payload.currency as UpdateProposalInput['feeCurrency'],
      expiresAt: payload.expiresAt,
      pricingMode: payload.pricingMode,
      lumpSumAmount: payload.pricingMode === 'LUMP_SUM' ? payload.lumpSumAmount ?? null : null,
      ...(recipients.length > 0 ? { recipients } : {}),
      items: payload.items,
      clauses: payload.clauses,
      billingSchedule: payload.billingSchedule ?? null,
      billingStartDate: payload.billingStartDate ?? null,
    });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-bg-base border border-border rounded-xl w-full max-w-4xl mx-4 shadow-2xl animate-pop p-5 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold text-white">Edit proposal</h2>
          <button onClick={onClose} className="text-muted hover:text-white">✕</button>
        </div>
        <ProposalComposerForm
          mode="edit"
          initial={initial}
          statusBadge={
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted">Status:</span>
              <span className={`px-2 py-0.5 rounded text-xs ${STATUS_BADGE[proposal.status] ?? 'bg-bg-hover text-white'}`}>
                {PROPOSAL_STATUS_LABELS[proposal.status] ?? proposal.status}
              </span>
              <span className="text-xs text-muted">— saving resets it to Draft for re-send.</span>
            </div>
          }
          onSubmit={handleSubmit}
          submitting={submitting}
          submitLabel="Save changes"
          onCancel={onClose}
        />
      </div>
    </div>
  );
}

function ConfirmModal({ title, message, onClose, onConfirm, submitting }: {
  title: string;
  message: string;
  onClose: () => void;
  onConfirm: () => void;
  submitting: boolean;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-bg-panel border border-border rounded-xl w-full max-w-md max-h-[90vh] overflow-y-auto mx-4 shadow-2xl animate-pop p-5">
        <h2 className="text-base font-semibold text-white mb-2">{title}</h2>
        <p className="text-sm text-muted mb-4">{message}</p>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="text-sm text-muted hover:text-white px-3 py-1.5">Cancel</button>
          <button onClick={onConfirm} disabled={submitting} className="bg-rose-700 hover:bg-rose-600 text-oncolor text-sm font-medium px-4 py-1.5 rounded-lg disabled:opacity-50">{submitting ? 'Deleting…' : 'Delete'}</button>
        </div>
      </div>
    </div>
  );
}
